import api from './api'

export type WorkTab = 'replied' | 'active' | 'postponed' | 'closed'

export interface ChatWorkItem {
    chat_id: number
    lead_id?: number | null
    title: string
    username?: string | null
    geo?: string | null
    temperature?: string | null
    unread_count: number
    last_message_at?: string | null
    last_message_text?: string | null
    work_state: string
    work_comment?: string | null
    close_outcome?: string | null
    close_outcome_label?: string | null
    crm_warning?: string | null
    // Лид ответил, а статус в CRM Wiseway остался ожидающим.
    needs_status_update?: boolean
    status_hint_label?: string | null
    // Последний известный статус лида в CRM Wiseway.
    crm_status?: string | null
    crm_status_label?: string | null
    // Идентификатор карточки в CRM Wiseway: им оператор называет лида, когда
    // ник и телефон скрыты.
    crm_ww_id?: string | null
    contacts_hidden?: boolean
    // Почему с аккаунта диалога нельзя писать. Пусто - всё в порядке.
    account_issue?: string | null
    assigned_operator_id?: number | null
    assigned_operator_name?: string | null
    account_username?: string | null
    account_phone?: string | null
    account_health_status?: string | null
    photo_url?: string | null
    is_lead: boolean
    // Статус карточки в CRM - фрод: подсвечиваем красным.
    crm_fraud?: boolean
    // Итог «Добавить лида» для сообщения.
    result_message?: string | null
    // Проверка диалога без карточки на дубль. null - ещё не проверяли.
    duplicate?: DuplicateCheck | null
}

export interface PlainChatMessage {
    id: number
    tg_message_id?: number | null
    text?: string | null
    text_html?: string | null
    date?: string | null
    outgoing: boolean
    operator_name?: string | null
    account_username?: string | null
    content_type?: string | null
    media_file_name?: string | null
    media_mime_type?: string | null
    media_size?: number | null
    media_file_path?: string | null
    reply_to?: number | null
    is_read: boolean
    is_edited: boolean
    deleted_by_lead: boolean
}

export interface DuplicateCheck {
    // done - проверено; failed - CRM Wiseway не ответила, проверится при «Добавить лида».
    state: 'done' | 'failed'
    error?: string | null
    is_duplicate: boolean
    crm?: {
        id: number
        status?: string | null
        status_label?: string | null
        owner_name?: string | null
        created_at?: string | null
        count: number
        fraud: boolean
        fraud_other_id?: number | null
    } | null
    lead?: {
        id: number
        crm_ww_id?: string | null
        owner_id?: number | null
        owner_name?: string | null
    } | null
    checked_at?: string | null
}

export interface DuplicateLead {
    id: number
    title?: string | null
    geo?: string | null
    telegram_nickname?: string | null
    phone?: string | null
}

export interface LeadDraft {
    chat_id: number
    title?: string | null
    first_name?: string | null
    last_name?: string | null
    telegram_nickname?: string | null
    phone?: string | null
    // Название = TG username; заперто, если username есть.
    title_locked?: boolean
    // Источник по умолчанию - «Написал в ЛС в TG»; менеджер выбирает сам.
    source?: string | null
    // create - нового человека нет нигде; merge - дубль, объединяем;
    // merge_fraud - самая старая карточка во фроде; unavailable - CRM не ответила.
    scenario: 'create' | 'merge' | 'merge_fraud' | 'unavailable'
    duplicate?: DuplicateCheck | null
}

export interface MergeLeadResult {
    // null - диалог ушёл другому ответственному и нажавшему больше не виден.
    item: ChatWorkItem | null
    lead_id: number
    message: string
    warnings: string[]
    fraud: boolean
}

export interface CloseReasonGroup {
    group: string
    reasons: { code: string; label: string }[]
}

export const chatWorkspaceApi = {
    // Фильтры и подгрузка идут на сервере: список может быть длиннее того, что
    // помещается в одну выдачу, и фильтровать загруженную пачку нельзя -
    // лид за её пределами просто не найдётся.
    getChats: (params: {
        tab: WorkTab
        search?: string
        geo?: string
        folder_id?: number
        sort?: 'activity' | 'unread' | 'name'
        limit?: number
        offset?: number
        cursor_at?: string
        cursor_id?: number
    }, signal?: AbortSignal) =>
        api.get<ChatWorkItem[]>('/chat-workspace', { params, signal }),

    getCounts: (params?: { geo?: string; folder_id?: number }) =>
        api.get<Record<string, number>>('/chat-workspace/counts', { params }),

    // Один диалог для прямой ссылки /chats/<id>. Сервер отдаёт его, только если
    // он виден пользователю хотя бы в одной вкладке.
    getItem: (chatId: number) => api.get<ChatWorkItem>(`/chat-workspace/item/${chatId}`),
    // Переписка диалога без карточки: порции с курсором, медиа, отправка файла.
    getPlainMessages: (chatId: number, params: { limit?: number; before_date?: string; before_id?: number }) =>
        api.get<PlainChatMessage[]>(`/chat-workspace/item/${chatId}/messages`, { params }),
    plainMediaUrl: (mediaPath: string) =>
        `/api/chat-workspace/item/${mediaPath}?token=${localStorage.getItem('token') || ''}`,
    sendPlainMedia: (chatId: number, file: File, caption?: string) => {
        const form = new FormData()
        form.append('file', file)
        if (caption) form.append('caption', caption)
        return api.post<PlainChatMessage>(`/chat-workspace/item/${chatId}/send-media`, form)
    },
    recheckDuplicate: (chatId: number) => api.post<ChatWorkItem>(`/chat-workspace/item/${chatId}/duplicate-check`),

    // Диалог лида - для старых ссылок вида /chats?leadId=.
    getItemByLead: (leadId: number) => api.get<ChatWorkItem>(`/chat-workspace/by-lead/${leadId}`),

    // Непрочитанные по папкам считает база - цифра не зависит от того,
    // сколько строк успел подгрузить браузер.
    getFolderUnread: (params: { tab: WorkTab; geo?: string }) =>
        api.get<Record<string, number>>('/chat-workspace/folder-unread', { params }),

    getOutcomes: () => api.get<{ groups: CloseReasonGroup[] }>('/chat-workspace/outcomes'),

    take: (chatId: number) => api.post<ChatWorkItem>(`/chat-workspace/${chatId}/take`),

    postpone: (chatId: number, comment: string) =>
        api.post<ChatWorkItem>(`/chat-workspace/${chatId}/postpone`, { comment }),

    close: (chatId: number, outcome: string, comment?: string) =>
        api.post<ChatWorkItem>(`/chat-workspace/${chatId}/close`, { outcome, comment }),

    // Для закрытого диалога причина обязательна, для отложенного не нужна.
    reopen: (chatId: number, outcome?: string, comment?: string) =>
        api.post<ChatWorkItem>(`/chat-workspace/${chatId}/reopen`, { outcome, comment }),

    // Что не так с аккаунтом диалога и кем его можно заменить. Ничего не меняет.
    getAccountReplacement: (chatId: number) =>
        api.get<{
            reason: string | null
            current_account: { id: number | null; username: string | null; phone: string | null }
            replacement: { id: number; username: string | null; phone: string | null } | null
        }>(`/chat-workspace/${chatId}/account-replacement`),

    // Продолжить переписку с другого аккаунта - только по нажатию оператора.
    switchAccount: (chatId: number, accountId: number) =>
        api.post<ChatWorkItem>(`/chat-workspace/${chatId}/switch-account`, { account_id: accountId }),

    // Передача лида другому сотруднику из диалога. Доступна каждому: свой или
    // ещё свободный диалог отдаёт оператор, любой - тимлид и суперадмин.
    transfer: (chatId: number, assignedToId: number) =>
        api.post<ChatWorkItem>(`/chat-workspace/${chatId}/transfer`, { assigned_to_id: assignedToId }),

    getReopenOutcomes: () => api.get<{ groups: CloseReasonGroup[] }>('/chat-workspace/reopen-outcomes'),

    // Управление лидом из раздела «Карточки лидов»: ответственный и перенос
    // между состояниями. Только для тимлида и суперадмина.
    leadWorkflow: (leadId: number, data: {
        action: 'assign' | 'replied' | 'active' | 'postponed' | 'closed'
        assigned_to_id?: number
        outcome?: string
        comment?: string
    }) => api.post<{ success: boolean; work_state: string; crm_warning?: string | null }>(
        `/chat-workspace/leads/${leadId}/workflow`, data
    ),

    getLeadDraft: (chatId: number) => api.get<LeadDraft>(`/chat-workspace/${chatId}/lead-draft`),

    attachLead: (chatId: number, leadId: number) =>
        api.post<ChatWorkItem>(`/chat-workspace/${chatId}/attach-lead`, { lead_id: leadId }),

    // Завести карточку прямо из диалога: создание и привязка одним запросом,
    // со своим правом - отдельно от общего запрета ручного создания.
    createLeadFromChat: (chatId: number, data: Record<string, unknown>) =>
        api.post<ChatWorkItem>(`/chat-workspace/${chatId}/create-lead`, data),

    // Дубль: объединить диалог с самой старой карточкой (CRM Wiseway или наша).
    mergeLeadFromChat: (chatId: number) =>
        api.post<MergeLeadResult>(`/chat-workspace/${chatId}/merge-lead`),

    // Окно «Изменить статус»: полный справочник статусов CRM Wiseway и текущее
    // значение, прочитанное у них напрямую.
    getCrmStatusOptions: (chatId: number) =>
        api.get<{
            groups: CloseReasonGroup[]
            current_status: string | null
            current_label: string | null
            has_crm_card: boolean
        }>(`/chat-workspace/${chatId}/crm-status`),

    setCrmStatus: (chatId: number, status: string) =>
        api.post<ChatWorkItem>(`/chat-workspace/${chatId}/crm-status`, { status }),

    // Насколько свежи показанные статусы CRM. Статус в шапке и в карточке -
    // копия последнего чтения их базы: если чтение сорвалось, копия остаётся на
    // экране и без этой отметки выглядит актуальной.
    getCrmStatusFreshness: () =>
        api.get<{
            enabled: boolean
            stale: boolean
            last_read_at: string | null
            minutes_ago: number | null
        }>('/chat-workspace/crm-status-freshness'),
}
